import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const imgUrl = (u) => {
  if (!u) return '';
  return u.startsWith('/api') ? `${API_URL}${u}` : u;
};

const linesToArr = (s) => s.split('\n').map((x) => x.trim()).filter(Boolean);

const ImageField = ({ label, value, onChange, authToken, testid }) => {
  const [uploading, setUploading] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API_URL}/api/image-upload/feature-image`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}` },
        body: fd,
      });
      const d = await res.json();
      if (d.success && d.url) { onChange(d.url); toast.success('📷 Screenshot upload हो गया'); }
      else toast.error(d.detail || 'Upload विफल');
    } catch (e) { toast.error('Upload error'); }
    finally { setUploading(false); }
  };
  return (
    <div className="space-y-2" data-testid={testid}>
      <p className="text-xs font-semibold text-slate-600">{label}</p>
      <div className="flex items-center gap-3">
        {value ? (
          <img src={imgUrl(value)} alt={label} className="w-28 h-20 object-cover rounded-lg border border-slate-200" />
        ) : (
          <div className="w-28 h-20 rounded-lg border border-dashed border-slate-300 flex items-center justify-center text-[10px] text-slate-400">कोई image नहीं</div>
        )}
        <label className="text-xs px-3 py-2 rounded-lg bg-indigo-50 text-indigo-700 font-semibold cursor-pointer hover:bg-indigo-100">
          {uploading ? '⏳...' : '⬆ Screenshot upload'}
          <input type="file" accept="image/*" className="hidden" disabled={uploading}
            onChange={(e) => upload(e.target.files[0])} data-testid={`${testid}-input`} />
        </label>
      </div>
    </div>
  );
};

const Input = ({ label, value, onChange, testid }) => (
  <div>
    <p className="text-xs font-semibold text-slate-600 mb-1">{label}</p>
    <input value={value || ''} onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 focus:ring-2 focus:ring-indigo-300 outline-none"
      data-testid={testid} />
  </div>
);

const TextArea = ({ label, value, onChange, rows = 4, hint, testid }) => (
  <div>
    <p className="text-xs font-semibold text-slate-600 mb-1">{label}{hint && <span className="text-slate-400 font-normal"> · {hint}</span>}</p>
    <textarea value={value || ''} onChange={(e) => onChange(e.target.value)} rows={rows}
      className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 focus:ring-2 focus:ring-indigo-300 outline-none"
      data-testid={testid} />
  </div>
);

export default function DikshaDemoEditor({ token }) {
  const authToken = token || localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' };

  const [features, setFeatures] = useState([]);
  const [editedIds, setEditedIds] = useState([]);
  const [selId, setSelId] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/diksha/admin/demo-features`, { headers: { Authorization: `Bearer ${authToken}` } });
      const d = await res.json();
      setFeatures(d.features || []);
      setEditedIds(d.edited_ids || []);
      if (!selId && d.features?.length) setSelId(d.features[0].id);
    } catch (e) { toast.error('Demo features load नहीं हुए'); }
    finally { setLoading(false); }
  }, [authToken]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const f = features.find((x) => x.id === selId);
    if (f) {
      setForm({
        name: f.name, name_hi: f.name_hi,
        description: f.description, description_hi: f.description_hi,
        before_text: f.before_text, after_text: f.after_text,
        before_image: f.before_image, after_image: f.after_image,
        key_benefits: (f.key_benefits || []).join('\n'),
        demo_steps: (f.demo_steps || []).join('\n'),
      });
    }
  }, [selId, features]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        name: form.name, name_hi: form.name_hi,
        description: form.description, description_hi: form.description_hi,
        before_text: form.before_text, after_text: form.after_text,
        before_image: form.before_image, after_image: form.after_image,
        key_benefits: linesToArr(form.key_benefits),
        demo_steps: linesToArr(form.demo_steps),
      };
      const res = await fetch(`${API_URL}/api/diksha/admin/demo-features/${selId}`, { method: 'PUT', headers, body: JSON.stringify(body) });
      const d = await res.json();
      if (d.success) { toast.success('✅ Diksha demo script save हो गया'); await load(); }
      else toast.error(d.detail || 'Save विफल');
    } catch (e) { toast.error('Save error'); }
    finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm('इस feature का script + screenshots default पर वापस लाएँ?')) return;
    const res = await fetch(`${API_URL}/api/diksha/admin/demo-features/${selId}/reset`, { method: 'POST', headers });
    if (res.ok) { toast.success('Default पर reset हो गया'); await load(); }
  };

  if (loading) return <div className="p-6 text-center text-slate-400" data-testid="diksha-editor-loading">⏳ लोड हो रहा है...</div>;

  return (
    <div className="space-y-4" data-testid="diksha-demo-editor">
      <div className="bg-gradient-to-r from-pink-500 to-purple-600 rounded-2xl p-5 text-white">
        <h2 className="text-lg font-bold">🎬 Diksha Demo Editor</h2>
        <p className="text-sm text-white/90 mt-1">हर feature के लिए script (demo steps) और असली screenshots बदलें — Diksha demo में यही दिखेगा।</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Feature list */}
        <div className="lg:col-span-1 bg-white rounded-2xl border border-slate-200 p-3 space-y-1" data-testid="feature-list">
          {features.map((f) => (
            <button key={f.id} onClick={() => setSelId(f.id)}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center justify-between gap-2 ${selId === f.id ? 'bg-purple-600 text-white' : 'text-slate-700 hover:bg-slate-100'}`}
              data-testid={`feature-item-${f.id}`}>
              <span className="truncate">{f.name_hi || f.name}</span>
              {editedIds.includes(f.id) && <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${selId === f.id ? 'bg-white/25' : 'bg-amber-100 text-amber-700'}`}>edited</span>}
            </button>
          ))}
        </div>

        {/* Edit panel */}
        {form && (
          <div className="lg:col-span-3 bg-white rounded-2xl border border-slate-200 p-5 space-y-4" data-testid="feature-edit-panel">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input label="Name (English)" value={form.name} onChange={(v) => set('name', v)} testid="edit-name" />
              <Input label="नाम (Hindi)" value={form.name_hi} onChange={(v) => set('name_hi', v)} testid="edit-name-hi" />
            </div>
            <TextArea label="Description (Hindi)" value={form.description_hi} onChange={(v) => set('description_hi', v)} rows={2} testid="edit-desc-hi" />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <ImageField label="📷 Before Image (पुरानी स्थिति)" value={form.before_image} onChange={(v) => set('before_image', v)} authToken={authToken} testid="edit-before-image" />
              <ImageField label="📸 After Image (शिक्षा मित्र screenshot)" value={form.after_image} onChange={(v) => set('after_image', v)} authToken={authToken} testid="edit-after-image" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <TextArea label="Before text" value={form.before_text} onChange={(v) => set('before_text', v)} rows={2} testid="edit-before-text" />
              <TextArea label="After text" value={form.after_text} onChange={(v) => set('after_text', v)} rows={2} testid="edit-after-text" />
            </div>

            <TextArea label="🎯 Demo Steps (Diksha जो narrate करेगी)" hint="हर line = एक step" value={form.demo_steps} onChange={(v) => set('demo_steps', v)} rows={5} testid="edit-demo-steps" />
            <TextArea label="✨ Key Benefits" hint="हर line = एक benefit" value={form.key_benefits} onChange={(v) => set('key_benefits', v)} rows={4} testid="edit-key-benefits" />

            <div className="flex items-center gap-2 pt-1">
              <button onClick={save} disabled={saving}
                className="px-5 py-2 rounded-lg bg-purple-600 text-white text-sm font-semibold hover:bg-purple-700 disabled:opacity-50" data-testid="save-feature-btn">
                {saving ? '⏳ Save हो रहा है...' : '💾 Save'}
              </button>
              <button onClick={reset} className="px-4 py-2 rounded-lg border border-slate-200 text-slate-600 text-sm" data-testid="reset-feature-btn">↺ Default पर reset</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
